'use strict';

/**
 * PictView-DrawingsPage
 * ---------------------
 * The Drawings board surface (Tools group). A gallery of Excalidraw drawings, each stored as one
 * self-contained SVG Media blob (OwnerType 'Drawing', IDOwner 0, team-visible per customer). New and
 * edit both open the shared full-screen Excalidraw editor (app.showDrawingEditor); the editor round-trips
 * the whole scene through the SVG, so a card reopens exactly where it was left. Because a drawing is an
 * SVG, each card shows the real thing as its thumbnail.
 *
 * Reuses PictView-DrawingEditor (the overlay + save/load) and MediaAPI (list/delete/blobURL); this view
 * only owns the list. Pict conventions: state in AppData.DrawingsPage, {~TS:~} iteration, inline handlers.
 */

const libPictView = require('pict-view');
const libModal = require('pict-section-modal');

const _DefaultConfiguration =
{
	ViewIdentifier: 'Plansheet-DrawingsPage',
	DefaultRenderable: 'Plansheet-DrawingsPage',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,

	CSSPriority: 510,
	CSS: /*css*/`
		.ps-drawings-head { display: flex; align-items: center; gap: 12px; margin: 0 0 18px; }
		.ps-drawings-head h2 { flex: 1; font-size: 22px; font-weight: 600; margin: 0; color: var(--theme-color-text-primary, #1f2430); }
		.ps-drawings-new { font: inherit; font-size: 13px; border: 1px solid var(--theme-color-brand-primary, #2880a6); background: var(--theme-color-brand-primary, #2880a6); color: var(--theme-color-text-on-brand, #fff); border-radius: 7px; padding: 7px 14px; cursor: pointer; }
		.ps-drawings-new:hover { filter: brightness(1.06); }
		.ps-drawings-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; }
		.ps-drawing-card { position: relative; border: 1px solid var(--theme-color-border-light, #e7ecf0); border-radius: 10px; overflow: hidden; background: var(--theme-color-background-panel, #fff); cursor: pointer; display: flex; flex-direction: column; }
		.ps-drawing-card:hover { border-color: var(--theme-color-brand-primary, #2880a6); box-shadow: 0 2px 10px rgba(20,40,80,0.08); }
		.ps-drawing-thumb { aspect-ratio: 4 / 3; background: var(--theme-color-background-secondary, #f4f6f9); display: flex; align-items: center; justify-content: center; overflow: hidden; }
		.ps-drawing-thumb img { width: 100%; height: 100%; object-fit: contain; display: block; }
		.ps-drawing-name { padding: 9px 11px; font-size: 13px; font-weight: 600; color: var(--theme-color-text-primary, #1f2430); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; border-top: 1px solid var(--theme-color-border-light, #eef1f5); }
		.ps-drawing-del { position: absolute; top: 6px; right: 6px; z-index: 2; width: 26px; height: 26px; border: none; border-radius: 6px; background: rgba(20,24,32,0.55); color: #fff; cursor: pointer; font-size: 15px; line-height: 1; display: inline-flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.15s ease; }
		.ps-drawing-card:hover .ps-drawing-del { opacity: 1; }
		.ps-drawing-del:hover { background: var(--theme-color-status-error, #c0392b); }
		.ps-drawings-empty { color: var(--theme-color-text-muted, #97a1ab); font-size: 14px; padding: 24px 4px; border: 1px dashed var(--theme-color-border-default, #d8dde6); border-radius: 10px; text-align: center; }
	`,

	Templates:
	[
		{
			Hash: 'Plansheet-DrawingsPage',
			Template: /*html*/`
<div class="ps-page ps-drawings" id="Plansheet-DrawingsPage-Root">
	<div class="ps-drawings-head">
		<h2>Drawings</h2>
		{~TS:DrawingsPage-New:AppData.DrawingsPage.NewSlot~}
	</div>
	<div class="ps-drawings-grid">{~TS:DrawingsPage-Card:AppData.DrawingsPage.Rows~}</div>
	{~TS:DrawingsPage-Empty:AppData.DrawingsPage.EmptySlot~}
</div>`
		},
		{
			Hash: 'DrawingsPage-New',
			Template: /*html*/`<button class="ps-drawings-new" onclick="_Pict.views['Plansheet-DrawingsPage'].newDrawing()">New drawing</button>`
		},
		{
			Hash: 'DrawingsPage-Card',
			Template: /*html*/`<div class="ps-drawing-card" onclick="_Pict.views['Plansheet-DrawingsPage'].editDrawing({~D:Record.IDMedia~})">{~TS:DrawingsPage-CardDel:Record.DelSlot~}<div class="ps-drawing-thumb"><img src="{~D:Record.URL~}" alt="{~D:Record.Title~}" loading="lazy" /></div><div class="ps-drawing-name">{~D:Record.Title~}</div></div>`
		},
		{
			Hash: 'DrawingsPage-CardDel',
			Template: /*html*/`<button class="ps-drawing-del" title="Delete drawing" onclick="event.stopPropagation();_Pict.views['Plansheet-DrawingsPage'].deleteDrawing({~D:Record.IDMedia~})">&times;</button>`
		},
		{
			Hash: 'DrawingsPage-Empty',
			Template: /*html*/`<div class="ps-drawings-empty">No drawings yet. Start one with &ldquo;New drawing&rdquo; to sketch and diagram on a full canvas.</div>`
		}
	],

	Renderables:
	[
		{ RenderableHash: 'Plansheet-DrawingsPage', TemplateHash: 'Plansheet-DrawingsPage', ContentDestinationAddress: '#Plansheet-Workspace', RenderMethod: 'replace' }
	]
};

class PictViewDrawingsPage extends libPictView
{
	onBeforeInitialize()
	{
		this._rows = [];
		return super.onBeforeInitialize();
	}

	_media() { return this.pict.providers.MediaAPI; }

	_canEdit()
	{
		let tmpApp = this.pict.PictApplication;
		return !!(tmpApp && typeof tmpApp._can === 'function' && tmpApp._can('content.edit'));
	}

	// Route entry: load the team's drawings, then paint.
	show() { return this.reload(); }

	reload()
	{
		this._ensureModal();
		let tmpMedia = this._media();
		if (!tmpMedia) { this._rows = []; this._shape(); this.render(); return Promise.resolve(); }
		return tmpMedia.listForOwner('Drawing', 0)
			.then((pRows) =>
			{
				// Newest first, so a just-saved drawing lands at the top.
				this._rows = (Array.isArray(pRows) ? pRows : []).slice().sort((pA, pB) => (pB.IDMedia - pA.IDMedia));
				this._shape();
				this.render();
			})
			.catch(() => { this._rows = []; this._shape(); this.render(); });
	}

	_shape()
	{
		let tmpMedia = this._media();
		let tmpCanEdit = this._canEdit();
		this.pict.AppData.DrawingsPage =
		{
			ViewHash: this.Hash,
			NewSlot: tmpCanEdit ? [{ ViewHash: this.Hash }] : [],
			Rows: this._rows.map((pRow) => (
			{
				IDMedia: pRow.IDMedia,
				Title: pRow.Title || pRow.OriginalFileName || ('Drawing ' + pRow.IDMedia),
				URL: tmpMedia ? tmpMedia.blobURL(pRow.IDMedia) : ('/1.0/Media/' + pRow.IDMedia + '/Blob'),
				DelSlot: tmpCanEdit ? [{ IDMedia: pRow.IDMedia }] : []
			})),
			EmptySlot: this._rows.length ? [] : [1]
		};
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	// Opening a drawing is a NAVIGATION -- new -> /drawings/new, existing -> /drawing/:id -- so the open
	// Excalidraw canvas deep-links and survives a reload (it used to open imperatively over /drawings, so a
	// reload discarded the whole canvas). The route calls openDrawingRoute to actually mount the editor.
	newDrawing()
	{
		if (!this._canEdit()) { return; }
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showNewDrawing === 'function') { tmpApp.showNewDrawing(); }
		else { this.openDrawingRoute(0); }
	}

	// The editor resolved a save with the drawing's { IDMedia, Title, URL }. Reconcile with the server,
	// then GUARANTEE the just-saved card is present: a plain re-read right after the write can miss the
	// just-created row (read-after-write / proxy timing), which showed as an empty gallery until a manual
	// reload. We know the row exists (the save resolved), so if the reconcile fetch missed it, we splice it
	// in from the definitive save result -- done after reload() so its result cannot clobber the card.
	_onSaved(pResult)
	{
		let tmpID = (pResult && pResult.IDMedia) ? pResult.IDMedia : 0;
		let tmpTitle = (pResult && pResult.Title) || 'Drawing';
		this.reload().then(() =>
		{
			if (tmpID && !this._rows.some((pRow) => pRow.IDMedia === tmpID))
			{
				this._rows.unshift({ IDMedia: tmpID, Title: tmpTitle });
				this._shape();
				this.render();
			}
		}).catch(() => {});
	}

	editDrawing(pIDMedia)
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showDrawing === 'function') { tmpApp.showDrawing(pIDMedia); }
		else { this.openDrawingRoute(pIDMedia); }
	}

	// Route entry for /drawing/:id and /drawings/new: mount the Excalidraw editor overlay for the drawing
	// (0 = a new one). Cancel / save / close navigates back to /drawings (via the onClose hook), so the URL
	// leaves the open drawing. Read-only users still open the canvas to look; their save is denied server-side.
	openDrawingRoute(pIDMedia)
	{
		let tmpApp = this.pict.PictApplication;
		if (!tmpApp || typeof tmpApp.showDrawingEditor !== 'function') { return; }
		let tmpID = Number(pIDMedia) || 0;
		let tmpRow = this._rows.find((pRow) => pRow.IDMedia === tmpID);
		let tmpTitle = tmpID ? ((tmpRow && (tmpRow.Title || tmpRow.OriginalFileName)) || 'Drawing') : 'Untitled drawing';
		let tmpSelf = this;
		tmpApp.showDrawingEditor(
			{
				OwnerType: 'Drawing', IDOwner: 0, IDMedia: tmpID, Title: tmpTitle, SaveLabel: 'Save',
				onClose: () => { if (tmpApp && typeof tmpApp.showDrawings === 'function') { tmpApp.showDrawings(); } }
			},
			(pResult) => tmpSelf._onSaved(pResult));
	}

	deleteDrawing(pIDMedia)
	{
		if (!this._canEdit()) { return; }
		let tmpMedia = this._media();
		if (!tmpMedia) { return; }
		this._confirm('Delete this drawing?', { title: 'Delete drawing', confirmLabel: 'Delete', dangerous: true })
			.then((pOk) => { if (pOk) { tmpMedia.remove(pIDMedia).then(() => this.show()).catch(() => {}); } });
	}

	_ensureModal()
	{
		if (!this.pict.views['Pict-Section-Modal']) { this.pict.addView('Pict-Section-Modal', libModal.default_configuration, libModal); }
		let tmpModalView = this.pict.views['Pict-Section-Modal'];
		if (tmpModalView && typeof document !== 'undefined' && document.body && typeof tmpModalView.initialize === 'function') { tmpModalView.initialize(); }
	}

	_confirm(pMessage, pOptions)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.confirm === 'function') { return tmpModal.confirm(pMessage, pOptions || {}); }
		return Promise.resolve(true);
	}
}

module.exports = PictViewDrawingsPage;
module.exports.default_configuration = _DefaultConfiguration;
